import { action, computed, observable, makeAutoObservable } from 'mobx';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import {
    PromotedEventsState,
    CompetitionCombinedType,
    EventCombinedType,
} from 'src/AppState/CmsState/PromotedEventsState/PromotedEventsState';
import { convertToNumber, validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getAllPromotedSports';
import { TrpcClient } from 'src/AppState/TrpcClient';

export type PromotedSportDataModelType = Response200Type[number];

export class PromotedEventFormState {
    @observable public eventId: FormInputState<string, number>;
    @observable private eventModelId: number | null;
    @observable public isButtonDisabled: boolean = false;
    @observable public showCreatedPopup: boolean = false;
    @observable public errorMessageForList: string | null = null;

    private readonly noEmptyValidationMessage = 'This field is required.';
    private readonly eventDoesntExistMessage = 'Incorrect Event ID. Event does not exist.';

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly promotedEventsSectionState: PromotedEventsState,
        private readonly modelsState: ModelsState
    ) {
        this.eventId = FormInputState.new('')
            .map(validateNotEmpty(this.noEmptyValidationMessage))
            .map(this.validateEventAlreadyExists())
            .map(this.validateEventDoesntExist(this.eventDoesntExistMessage))
            .map(convertToNumber('Event ID must be a number.'));
        this.eventModelId = null;
        makeAutoObservable(this);
    }

    @action private setOpenCreatedSections = (): void => {
        if (this.eventFoundData !== null) {
            const sportState = this.promotedEventsSectionState.getPromotedEventSportStateBySportSlug(
                this.eventFoundData.sport
            );
            const competitionState = sportState.getPromotedEventCompStateByCompId(this.eventFoundData.competition);

            sportState.isOpenSection = true;
            competitionState.isOpenSection = true;
        }
    };

    @computed private get eventInputData(): EventModel | null {
        const eventId = this.eventId.value === '' ? 0 : parseInt(this.eventId.value);
        return this.modelsState.getEvent(eventId);
    }

    // if null - sport and competition don't exist. Create sport and competition
    @computed private get singleSportData(): PromotedSportDataModelType | null {
        const sportSlug = this.eventInputData?.sport;
        if (sportSlug === undefined || this.promotedEventsSectionState.promotedEventsData === null) {
            return null;
        }
        return this.promotedEventsSectionState.promotedEventsData.find((s) => s.sport_id === sportSlug) ?? null;
    }

    // list of all added competitions for the sport
    @computed private get singleSportCompetitionsData(): PromotedSportDataModelType['competitions_list'] {
        return this.singleSportData?.competitions_list ?? [];
    }

    // if null - create competition
    @computed private get singleCompetitionData(): CompetitionCombinedType | null {
        const competitionId = this.eventInputData?.competition;
        return (
            this.singleSportCompetitionsData.find((competition) => competition.competition_id === competitionId) ?? null
        );
    }

    // list of all added events for the competition
    @computed private get singleCompetitionEventsData(): Array<EventCombinedType> {
        return this.singleCompetitionData?.events_list ?? [];
    }

    @computed private get sportDisplayOrder(): number {
        const lengthOfSports = this.promotedEventsSectionState.promotedEventsData?.length ?? 1;
        const topElem = this.promotedEventsSectionState.sportsArray[lengthOfSports - 1];
        return topElem === undefined ? 1 : topElem.display_order + 1;
    }

    @computed private get competitionDisplayOrder(): number {
        const lengthOfCompetitions = this.singleSportCompetitionsData.length;
        const topElem = this.singleSportCompetitionsData[lengthOfCompetitions - 1];
        return topElem === undefined ? 1 : topElem.display_order + 1;
    }

    @computed private get eventDisplayOrder(): number {
        const lengthOfEvents = this.singleCompetitionEventsData.length;
        const topElem = this.singleCompetitionEventsData[lengthOfEvents - 1];
        return topElem === undefined ? 1 : topElem.display_order + 1;
    }

    @action private setIsButtonDisabled = (v: boolean): void => {
        this.isButtonDisabled = v;
    };

    @action public handleCreateEvent = async (): Promise<void> => {
        await this.promotedEventsSectionState.promotedEventsResource.refresh();
        this.promotedEventsSectionState.setSportsDataFromResource();

        this.setIsButtonDisabled(true);

        this.eventId.setAsVisited();
        const { result } = this.eventId;

        if (result.value.type === 'ok' && this.eventFoundData !== null) {
            const promotedEventDetailsData = {
                event_id: result.value.data,
                is_active: true,
                display_order: this.eventDisplayOrder,
                sport_id: this.eventFoundData.sport,
                competition_id: this.eventFoundData.competition,
                market_id: null,
                start_time: this.eventFoundData.timeSettingsStartTime,
            };

            const promotedCompetitionDetailsData = {
                competition_id: this.eventFoundData.competition,
                is_active: false,
                display_order: this.competitionDisplayOrder,
                sport_id: this.eventFoundData.sport,
            };

            const promotedSportDetailsData = {
                sport_id: this.eventFoundData.sport,
                is_active: false,
                display_order: this.sportDisplayOrder,
            };

            this.setOpenCreatedSections();

            try {
                const submitEventResult = await this.trpcClient.client.promotedEvents.createPromotedEvent.mutate({
                    data: promotedEventDetailsData,
                });

                if (this.singleSportData === null && this.singleCompetitionData === null) {
                    const submitSportResult = await this.trpcClient.client.promotedSports.createPromotedSport.mutate({
                        data: promotedSportDetailsData,
                    });

                    const submitCompetitionResult =
                        await this.trpcClient.client.promotedCompetitions.createPromotedCompetition.mutate({
                            data: promotedCompetitionDetailsData,
                        });

                    if (
                        submitEventResult.status === 200 &&
                        submitSportResult.status === 200 &&
                        submitCompetitionResult.status === 200
                    ) {
                        await this.promotedEventsSectionState.promotedEventsResource.refresh();
                        this.promotedEventsSectionState.setSportsDataFromResource();
                        this.eventId.setValue('');
                        this.eventId.reset();
                        this.eventModelId = null;
                        this.showCreatedPopup = true;
                    } else {
                        return this.handleError('Something went wrong.');
                    }
                }

                if (this.singleSportData !== null && this.singleCompetitionData === null) {
                    const submitCompetitionResult =
                        await this.trpcClient.client.promotedCompetitions.createPromotedCompetition.mutate({
                            data: promotedCompetitionDetailsData,
                        });

                    if (submitEventResult.status === 200 && submitCompetitionResult.status === 200) {
                        await this.promotedEventsSectionState.promotedEventsResource.refresh();
                        this.promotedEventsSectionState.setSportsDataFromResource();
                        this.eventId.setValue('');
                        this.eventId.reset();
                        this.eventModelId = null;
                        this.showCreatedPopup = true;
                    } else {
                        return this.handleError('Something went wrong.');
                    }
                }

                if (submitEventResult.status === 200) {
                    await this.promotedEventsSectionState.promotedEventsResource.refresh();
                    this.promotedEventsSectionState.setSportsDataFromResource();
                    this.eventId.setValue('');
                    this.eventId.reset();
                    this.eventModelId = null;
                    this.showCreatedPopup = true;
                } else {
                    return this.handleError('Something went wrong.');
                }
            } catch (error) {
                console.error(error);
            }
        }

        this.setIsButtonDisabled(false);
    };

    @action public handleHideCreatedPopup = (): void => {
        this.showCreatedPopup = false;
    };

    @computed public get isEventValid(): boolean {
        const eventId = this.eventId.result;
        return eventId.value.type === 'error' && this.eventId.isVisited() ? false : true;
    }

    @action public handleFindEvent = async (): Promise<void> => {
        this.eventModelId = parseInt(this.eventId.value);
    };

    @computed public get eventFoundData(): EventModel | null {
        if (this.eventModelId !== null && this.eventModelId === parseInt(this.eventId.value)) {
            return this.modelsState.getEvent(this.eventModelId);
        }
        return null;
    }

    @computed public get sportName(): string {
        const sportSlug = this.eventInputData?.sport ?? '';
        return this.modelsState.getSport(sportSlug)?.name ?? '';
    }

    @computed public get competitionName(): string {
        const competitionId = this.eventInputData?.competition ?? null;

        if (competitionId !== null) {
            return this.modelsState.getCompetitionModel(competitionId)?.name ?? '';
        }

        return '';
    }

    @computed public get eventName(): string {
        return this.eventInputData?.name ?? '';
    }

    @action private validateEventAlreadyExists =
        () =>
        (value: string): Result<string> => {
            const event = this.singleCompetitionEventsData.find(
                (elem) => elem.event_id === parseInt(this.eventId.value)
            );

            if (event !== undefined) {
                const message = `Event ${this.eventName} has already been added to the ${this.sportName} section under the ${this.competitionName} competition.`;
                return Result.createError(message);
            }

            return Result.createOk(value);
        };

    @action private validateEventDoesntExist =
        (message: string) =>
        (value: string): Result<string> => {
            if (this.eventInputData === null) {
                return Result.createError(message);
            }
            return Result.createOk(value);
        };

    @action private handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await timeout(5000);
        this.errorMessageForList = null;
    };
}
